<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'

const props = defineProps({
  icon: {
    type: Array,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  headerLabel: {
    type: String,
    default: null
  },
  headerSubtext: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingText: {
    type: String,
    default: 'Loading...'
  },
  sidebarExpanded: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['open', 'close'])

const isOpen = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})

const displayHeaderLabel = computed(() => props.headerLabel || props.label)

// Position the menu to the left of the trigger
function updateMenuPosition() {
  if (!triggerRef.value || !isOpen.value) return

  const rect = triggerRef.value.getBoundingClientRect()
  const menuWidth = 280

  // Always open to the left of the sidebar
  let left = rect.left - menuWidth - 8

  // Boundary check
  if (left < 10) left = 10

  menuStyle.value = {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${left}px`,
    minWidth: `${menuWidth}px`
  }
}

function toggle() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

// Close when clicking outside
function handleClickOutside(event) {
  if (triggerRef.value && triggerRef.value.contains(event.target)) {
    return
  }
  if (menuRef.value && menuRef.value.contains(event.target)) {
    return
  }
  close()
}

// Close on escape
function handleKeydown(event) {
  if (event.key === 'Escape') {
    close()
  }
}

// Update position when opened
watch(isOpen, async (newVal) => {
  if (newVal) {
    await nextTick()
    updateMenuPosition()
    emit('open')
  } else {
    emit('close')
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})

// Expose close method to parent
defineExpose({ close, isOpen })
</script>

<template>
  <li class="nav-item-wrapper sidebar-dropdown-wrapper">
    <div
      ref="triggerRef"
      class="sidebar-nav"
      :class="{ 'disabled': disabled, 'active': isOpen }"
      @click="toggle"
    >
      <font-awesome-icon class="icon" :icon="icon" />
      <span class="nav-text">{{ label }}</span>
      <div class="nav-popout" v-if="!sidebarExpanded && !isOpen">
        <span>{{ label }}</span>
      </div>
    </div>

    <!-- Menu (teleported to body) -->
    <Teleport to="body">
      <Transition name="sidebar-dropdown">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="sidebar-dropdown-menu"
          :style="menuStyle"
        >
          <!-- Loading overlay -->
          <div v-if="loading" class="sidebar-dropdown-loading">
            <div class="spinner"></div>
            <span>{{ loadingText }}</span>
          </div>

          <!-- Header -->
          <div class="sidebar-dropdown-header">
            <font-awesome-icon class="header-icon" :icon="icon" />
            <div class="header-text">
              <span class="header-label">{{ displayHeaderLabel }}</span>
              <span v-if="headerSubtext" class="header-subtext">{{ headerSubtext }}</span>
            </div>
          </div>

          <!-- Menu content -->
          <div class="sidebar-dropdown-content" :class="{ 'is-loading': loading }">
            <slot></slot>
          </div>
        </div>
      </Transition>
    </Teleport>
  </li>
</template>

<style scoped>
.sidebar-dropdown-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

.sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: #7A8696;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  border-radius: 0.75rem;
  position: relative;
  gap: 0;
  cursor: pointer;
}

.sidebar-nav.disabled {
  color: #4a5568;
  cursor: not-allowed;
}

.sidebar-nav.active {
  background-color: #1d2531;
  color: white;
}

.sidebar-nav:not(.disabled):hover {
  color: white;
  background-color: #1d2531;
}

.sidebar-nav.disabled:hover {
  background-color: #1a1f28;
}

.sidebar-nav .icon {
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-text {
  display: none;
}

.nav-popout {
  display: flex;
  position: absolute;
  right: calc(100% - 1.3rem);
  left: auto;
  top: 0;
  height: 100%;
  background: #1d2531;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

.sidebar-dropdown-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

/* Muted popout for disabled items */
.sidebar-nav.disabled .nav-popout {
  background: #1a1f28;
  color: #4a5568;
}

/* Hide popout when dropdown is open */
.sidebar-nav.active + .nav-popout,
.sidebar-nav.active .nav-popout {
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>

<!-- Styles for expanded sidebar state - applied from parent -->
<style>
/* When sidebar is expanded */
#tool-sidebar.expanded .sidebar-dropdown-wrapper .sidebar-nav {
  justify-content: flex-start;
}

#tool-sidebar.expanded .sidebar-dropdown-wrapper .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

#tool-sidebar.expanded .sidebar-dropdown-wrapper .sidebar-nav .icon {
  margin-right: 0.75rem;
}

#tool-sidebar:not(.expanded) .sidebar-dropdown-wrapper .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

/* Dropdown menu styles */
.sidebar-dropdown-menu {
  background: #1a1f28;
  border: 1px solid #2a3441;
  border-radius: 0.75rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 9999;
  overflow: hidden;
  min-width: 200px;
}

.sidebar-dropdown-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.02);
}

.sidebar-dropdown-header .header-icon {
  font-size: 1rem;
  color: #7357D2;
  width: 1.2rem;
  flex-shrink: 0;
}

.sidebar-dropdown-header .header-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.sidebar-dropdown-header .header-label {
  font-size: 0.95rem;
  color: #E5E7EB;
  font-weight: 500;
}

.sidebar-dropdown-header .header-subtext {
  font-size: 0.75rem;
  color: #6B7280;
}

.sidebar-dropdown-content {
  position: relative;
  padding: 0.5rem 0;
}

.sidebar-dropdown-content.is-loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Loading overlay */
.sidebar-dropdown-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 31, 40, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  z-index: 10;
  color: #9CA3AF;
  font-size: 0.85rem;
}

.sidebar-dropdown-loading .spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #374151;
  border-top: 2px solid #7357D2;
  border-radius: 50%;
  animation: sidebar-dropdown-spin 1s linear infinite;
}

@keyframes sidebar-dropdown-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Dropdown animation */
.sidebar-dropdown-enter-active,
.sidebar-dropdown-leave-active {
  transition: all 0.2s ease;
}

.sidebar-dropdown-enter-from,
.sidebar-dropdown-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

/* Menu item styles */
.sidebar-dropdown-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease;
  color: #E5E7EB;
  font-size: 0.85rem;
}

.sidebar-dropdown-menu .dropdown-item:hover {
  background: rgba(115, 87, 210, 0.15);
}

.sidebar-dropdown-menu .dropdown-item .item-icon {
  color: #7357D2;
  width: 1rem;
  flex-shrink: 0;
}

.sidebar-dropdown-menu .dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.05);
  margin: 0.5rem 0;
}

.sidebar-dropdown-menu .dropdown-section {
  padding: 0.5rem 1rem 0.25rem;
  font-size: 0.7rem;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
